import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, TextField, Typography, useTheme } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { Fragment, FunctionComponent, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CBAttachmentFieldTypeNames, CBDossierKindKeys, DOSSIER_ATTACHMENT_FIELD_SORT_ORDER, DossierTemplateDto, FieldType, IDocParam, IGetCargosPlace, IShowFieldsParams} from "../../types";
import dayjs from "dayjs";
import FileUploader from "../../../../util/component/FileUploader";
import Autocomplete from "@mui/material/Autocomplete";
import { Controller, FormProvider, useFieldArray, useForm, useWatch, useFormContext } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { v4 } from "uuid";
import { getCargosPlaces } from "../../api/service/customerbaseApi";
import CustomLoader from "../../../../util/component/CustomLoader";
import { isValidFileType } from "../../utils";
import { GlobalContext } from "../../../../util/state/GlobalContextProvider";
import { setSnackbar } from "../../../../util/state/globalAction";
import { snackbarTypes } from "../../../../util/state/globalVariables";
import CutomerBaseAttachmentCustomFields from "../dossier/CutomerBaseAttachmentCustomFields";

interface DocumentModalProps {
    open: boolean,
    kindOptions: {
        key: string,
        value: string
    }[],
    template: DossierTemplateDto | undefined,
    updatingDoc?: IDocParam,
    attachment?: boolean,
    onSave: (docParams: IDocParam) => void,
    onClose: () => void
}

const DocumentModal: FunctionComponent<DocumentModalProps> = ({
    open,
    kindOptions,
    template,
    updatingDoc,
    attachment = false,
    onSave,
    onClose
}) => {

    const { dispatchGlobalData } = useContext(GlobalContext);
    const { control, setValue } = useFormContext<IDocParam>();
    const theme = useTheme();
    const { t } = useTranslation("customerbase");
    const [showFields, setShowFields] = useState<IShowFieldsParams>({
        showIssueDate: true,
        showExpireDate: true,
        showBackPage: true,
        showAttachmentFields: true
    });
    const [cargosPlaceOptions, setCargosPlaceOptions] = useState<IGetCargosPlace[] | []>([]);
    const [cargosPlaceCountryOptions, setCargosPlaceCountryOptions] = useState<IGetCargosPlace[] | []>([]);
    const [citizenshipCargosPlaceOptions, setCitizenshipCargosPlaceOptions] = useState<IGetCargosPlace[] | []>([]);
    const [loadingGetCargosPlaces, setLoadingGetCargosPlaces] = useState(false);


    const validationSchema = yup.object().shape({
        number: yup.string().nullable(true).required(t("message.validation.requiredField")).trim(),
        kindIdentifier: yup.string().nullable(true).required(t("message.validation.requiredField")).trim(),
        issueDate: yup.string().when(['kindIdentifier'], {
            is: (kindIdentifier: string) => !!kindIdentifier && kindIdentifier == CBDossierKindKeys.SELFIE,
            then: yup.string().nullable(true).notRequired(),
            otherwise: yup.string().nullable(true).required(t("message.validation.requiredField"))
        }),
        expireDate: yup.string().when(['kindIdentifier'], {
            is: (kindIdentifier: string) => !!kindIdentifier && kindIdentifier == CBDossierKindKeys.SELFIE,
            then: yup.string().nullable(true).notRequired(),
            otherwise: yup.string().nullable(true).required(t("message.validation.requiredField"))
        }),
        frontPage: yup.mixed().nullable(true).required(t("message.validation.requiredField")),
        backPage: yup.mixed().when(['kindIdentifier'], {
            is: (kindIdentifier: string) => !!kindIdentifier && kindIdentifier == CBDossierKindKeys.SELFIE,
            then: yup.mixed().nullable(true).notRequired(),
            otherwise: yup.mixed().nullable(true).required(t("message.validation.requiredField"))
        }),
        fields: yup.array().of(
            yup.object().shape({
                type: yup.object().shape({
                    name: yup.string().notRequired()

                }),
                /*value: yup.string().nullable().when(['type.name'], {
                    is: (name: string) => !!name && name == 'category',
                    then: yup.string().nullable(true).required(t("message.validation.requiredField"))
                }),*/
                value: yup.mixed().nullable(true).required(t("message.validation.requiredField"))
            })
        )
        
    });

    const form = useForm<IDocParam>({
        defaultValues : {
            owner: null,
            dossier: null,
            identifier: null,
            number: null,
            kindIdentifier: null,
            kindDescription: null,
            kindValue: null,
            dossierTemplateIdentifier: null,
            status: null,
            issueDate: null,
            expireDate: null,
            country: null,
            fields: [],
            frontPage: null,
            backPage: null,
            isNewFrontPage: null,
            isNewBackPage: null
        },
        resolver: yupResolver(validationSchema)
    });

    const attachmentFields = useFieldArray<IDocParam, 'fields'>({
        control: form.control,
        name: "fields"
    });

    const currentKind = useWatch<IDocParam, "kindIdentifier">({
        control: form.control, 
        name: "kindIdentifier"
    });

    const currentReleaseDate = useWatch<IDocParam, "issueDate">({
        control: form.control,
        name: "issueDate"
    });

    const currentExpireDate = useWatch<IDocParam, "expireDate">({
        control: form.control,
        name: "expireDate"
    });

    useEffect(() => {

        if(!updatingDoc) return;
        console.log("Updating Doc: ", updatingDoc);
        const issueDateField = updatingDoc?.fields?.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE);

        if(!!issueDateField && updatingDoc?.fields?.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUING_PLACE)) {
            handleIssuingPlaceByDate(issueDateField.value);
            handleIssuingPlaceCountryByDate(issueDateField.value);
        } else if (!!updatingDoc?.issueDate) {
            handleIssuingPlaceByDate(updatingDoc?.issueDate);
            handleIssuingPlaceCountryByDate(updatingDoc?.issueDate);
        } else {
            setCargosPlaceOptions([]);
            setCargosPlaceCountryOptions([]);
            setCitizenshipCargosPlaceOptions([]);
        }
            

        if(updatingDoc?.kindIdentifier == CBDossierKindKeys.SELFIE) {
            setShowFields({
                showIssueDate: false,
                showExpireDate: false,
                showBackPage: (template?.attachments.find(att => att.identifier == CBDossierKindKeys.SELFIE)?.pages?.length || 0) > 1,
                showAttachmentFields: (template?.attachments.find(att => att.identifier == CBDossierKindKeys.SELFIE)?.fields.length || 0) > 1
            });
        } else {
            setShowFields({
                showIssueDate: true,
                showExpireDate: true,
                showBackPage: true,
                showAttachmentFields: true
            });
        }

        form.reset({
            owner: updatingDoc?.owner || null,
            dossier: updatingDoc?.dossier || null,
            dossierTemplateIdentifier: updatingDoc?.dossierTemplateIdentifier || null,
            identifier: updatingDoc?.identifier || null,
            number: updatingDoc?.number || null,
            kindDescription: updatingDoc?.kindDescription || null,
            kindValue: updatingDoc?.kindValue || null,
            kindIdentifier: updatingDoc?.kindIdentifier || null,
            status: updatingDoc?.status || null,
            issueDate: updatingDoc?.issueDate || null,
            expireDate: updatingDoc?.expireDate || null,
            country: updatingDoc?.country || null,
            fields: updatingDoc?.fields?.map(f => ({
                owner: f.owner || null,
                dossier: f.dossier || null,
                attachment: f.attachment || null,
                type: {
                    title: f.type?.title || null,
                    name: f.type?.name || null,
                    description: f.type?.description || null,
                    type: f.type?.type || null,
                    values: !!f.type?.values ? [...f.type.values] : [],
                    tags: !!f.type?.tags ? [...f.type?.tags] : []
                },
                value: f.value || null,
                values: !!f.values ? [...f.values] : []
            })) || [],
            pages: updatingDoc?.pages?.map(page => ({
                owner: page.owner || null,
                dossier: page.dossier || null,
                attachment: page.attachment || null,
                type: page.type || null,
                status: page.status || null,
                origin: page.origin || null,
                originIdentifier: page.originIdentifier || null,
                creationTime: page.creationTime || null,
                updateTime: page.updateTime || null,
                note: page.note || null
            })) || [],
            frontPage: updatingDoc?.frontPage || null,
            backPage: updatingDoc?.backPage || null,
            isNewFrontPage: updatingDoc?.isNewFrontPage,
            isNewBackPage: updatingDoc?.isNewBackPage
        });
        

    }, [updatingDoc]);


    useEffect(() => {
        if(!open){
            form.reset({
                owner: null,
                dossier: null,
                dossierTemplateIdentifier: null,
                identifier: null,
                number: null,
                kindDescription: null,
                kindValue: null,
                kindIdentifier: null,
                status: null,
                issueDate: null,
                expireDate: null,
                country: null,
                fields: [],
                pages: [],
                frontPage: null,
                backPage: null,
                isNewFrontPage: null,
                isNewBackPage: null
            });
            setCargosPlaceOptions([]);
            setCitizenshipCargosPlaceOptions([]);
            form.clearErrors();

        }
    }, [open]);

    const handleIssuingPlaceByDate = (param?: string | null) => {
        if(!!param) {
            setLoadingGetCargosPlaces(true);
            getCargosPlaces(undefined, dayjs(param).format("YYYY-MM-DD")).then((resp) => {
                const data: IGetCargosPlace[] | [] = resp.data?.data || [];
                //console.log(data);
                setCargosPlaceOptions(data);
                setCitizenshipCargosPlaceOptions(data.filter((cpo) => cpo.provincia == "ES"));

            }).catch((err) => {
                setCargosPlaceOptions([]);
                setCitizenshipCargosPlaceOptions([]);
            }).finally(() => {
                setLoadingGetCargosPlaces(false);
            });
        }
    }

    const handleIssuingPlaceCountryByDate = (param?: string | null) => {
        if(!!param) {
            setLoadingGetCargosPlaces(true);
            getCargosPlaces('ES', dayjs(param).format("YYYY-MM-DD")).then((resp) => {
                const data: IGetCargosPlace[] | [] = resp.data?.data || [];
                //console.log(data);
                setCargosPlaceCountryOptions(data);

            }).catch((err) => {
                setCargosPlaceCountryOptions([]);
            }).finally(() => {
                setLoadingGetCargosPlaces(false);
            });
        }
    }

    return (
        <>
            <FormProvider {...form}>
                <Dialog open={open} onClose={() => { return; }} fullWidth maxWidth="lg">
                    <DialogTitle>
                        <Box sx={{display: "flex", alignItems: "center"}}>
                            <Typography color={"primary"} fontWeight={"bold"} sx={{margin: 1}}>
                                    {!!updatingDoc ? 
                                        (attachment ? t("documentModal.attachment.updatingTitle") : (t("documentModal.genericUpdatingTitle") + " " + ( !!currentKind ? t(`attachmentKindType.${currentKind}`) : ''))) 
                                        : (attachment? t("documentModal.attachment.creatingTitle") : t("documentModal.creatingTitle"))}
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                    { attachment ?
                        <Box>
                            <FileUploader
                                onUpload={(frontPage: File) => /*handleChangeDocParams(frontPage, "frontPage")*/ null} 
                            />
                        </Box>
                        :
                        <Grid container spacing={2}>
                            <Grid item container id="main-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4}>
                                {!updatingDoc && 
                                    <Grid item md={3} xs={12}>
                                        <Controller 
                                            control={form.control}
                                            name="kindIdentifier"
                                            render={({field, fieldState}) => (
                                                <Autocomplete
                                                    disabled={!!updatingDoc}
                                                    fullWidth
                                                    id="kind"
                                                    options={kindOptions}
                                                    renderInput={(params: any) =>
                                                        <TextField 
                                                            fullWidth
                                                            {...params}
                                                            label={t("docType")}
                                                            required
                                                            error={Boolean(fieldState.error)}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    }
                                                    //value={field.value || null}
                                                    value={field.value ? kindOptions.find((c) => c.key == field.value) || null : null}
                                                    getOptionLabel={(option: any) => t(`attachmentKindType.${option.key}`)}
                                                    onChange={(_event, value) => {
                                                        
                                                        const showOtherPages: boolean = (template?.attachments.find(att => att.identifier == value?.key)?.pages?.length || 0) > 1;
                                                        const fields: FieldType[] = template?.attachments.find(att => att.identifier == value?.key)?.fields || [];
                                                        const tempFields: FieldType[] = fields.length > 0 ? fields.filter(f => !f.name.includes(CBAttachmentFieldTypeNames.MCTC) && !f.name.includes(CBAttachmentFieldTypeNames.DE) && !f.name.includes(CBAttachmentFieldTypeNames.DNS) && !f.name.includes(CBAttachmentFieldTypeNames.LIVENESS)) : []; //AL MOMENTO PER LA LICENZA GESTIAMO SOLO LA CATEGORY
                                                        const title: string = template?.attachments.find(att => att.identifier == value?.key)?.title || '';
                                                        
                                                        
                                                        form.clearErrors();

                                                        form.setValue("number", value?.key == CBDossierKindKeys.SELFIE ? Date.now().toString() : null);
                                                        form.setValue("issueDate", null);
                                                        form.setValue("expireDate", null);
                                                        form.setValue("frontPage", null);
                                                        form.setValue("backPage", null);

                                                        form.setValue("fields", tempFields.sort((a,b) => DOSSIER_ATTACHMENT_FIELD_SORT_ORDER.indexOf(a.name || '') - DOSSIER_ATTACHMENT_FIELD_SORT_ORDER.indexOf(b.name || '')).map(f => ({
                                                            type: {
                                                                title: f.title || null,
                                                                name: f.name || null,
                                                                description: f.description || null,
                                                                type: f.type || null,
                                                                values: [...f.values],
                                                                tags: [...f.tags]
                                                            },
                                                            value: null,
                                                            values: []
                                                        })));

                                                        form.setValue("kindDescription", title);
                                                        form.setValue("kindIdentifier", value?.key);
                                                        form.setValue("kindValue", value?.value);

                                                        form.setValue("dossierTemplateIdentifier", template?.identifier || null);
                                                        
                                                        setCargosPlaceOptions([]);
                                                        setCitizenshipCargosPlaceOptions([]);
                                                        switch (value?.key) {
                                                            case CBDossierKindKeys.LICENCE:
                                                            case CBDossierKindKeys.IDENTITY_DOCUMENT_PASSPORT:
                                                            case CBDossierKindKeys.IDENTITY_DOCUMENT_CARD:
                                                            case CBDossierKindKeys.INTERNATIONAL_DRIVING_LICENCE:
                                                            case CBDossierKindKeys.INTERNATIONAL_TRANSLATION:
                                                                setShowFields({
                                                                    showIssueDate: true,
                                                                    showExpireDate: true,
                                                                    showBackPage: showOtherPages,
                                                                    showAttachmentFields: fields.length > 0
                                                                });                                                        
                                                                break;
                                                            case CBDossierKindKeys.SELFIE:
                                                                setShowFields({
                                                                    showIssueDate: false,
                                                                    showExpireDate: false,
                                                                    showBackPage: showOtherPages,
                                                                    showAttachmentFields: fields.length > 0
                                                                });
                                                                break;
                                                            /*case CBDossierKindKeys.IDENTITY_DOCUMENT_PASSPORT:
                                                                setShowFields({
                                                                    showIssueDate: true,
                                                                    showExpireDate: true,
                                                                    showBackPage: showOtherPages,
                                                                    showAttachmentFields: fields.length > 0
                                                                });
                                                                break;
                                                            case CBDossierKindKeys.IDENTITY_DOCUMENT_CARD:
                                                                setShowFields({
                                                                    showIssueDate: true,
                                                                    showExpireDate: true,
                                                                    showBackPage: showOtherPages,
                                                                    showAttachmentFields: fields.length > 0
                                                                });
                                                                break;*/
                                                        
                                                            default:
                                                                setShowFields({
                                                                    showIssueDate: true,
                                                                    showExpireDate: true,
                                                                    showBackPage: true,
                                                                    showAttachmentFields: fields.length > 0
                                                                });
                                                                break;
                                                        }

                                                    }}
                                                />
                                            )}
                                        />
                                    </Grid>
                                }
                                {!!currentKind && (currentKind !== CBDossierKindKeys.SELFIE) &&
                                    <Grid item md={3} xs={12}>
                                        <Controller
                                            control={form.control}
                                            name="number"
                                            render={({field, fieldState}) => (
                                                <TextField 
                                                    fullWidth
                                                    id="number"
                                                    label={t("idNumber")}
                                                    required
                                                    value={field.value || ''}
                                                    onChange={(value) => {
                                                        field.onChange(value);
                                                    }}
                                                    error={Boolean(fieldState.error)}
                                                    helperText={fieldState.error?.message}
                                                /> 
                                            )}
                                        />
                                    </Grid>
                                }
                                {!!currentKind && showFields.showIssueDate &&
                                    <Grid item md={3} xs={12} >
                                        <Controller 
                                            control={form.control}
                                            name="issueDate"
                                            render={({field, fieldState}) => (
                                                <DatePicker
                                                    value={field.value || null}
                                                    onChange={(value) => {
                                                        field.onChange(dayjs(value).format("YYYY-MM-DD"));

                                                        if(!!currentExpireDate && dayjs(value).isAfter(dayjs(currentExpireDate))) {
                                                            form.setValue("expireDate", null, {shouldDirty: true});
                                                        }

                                                        if(currentKind == CBDossierKindKeys.LICENCE) {
                                                            const index = attachmentFields.fields.findIndex((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE);
                                                            const firstReleaseDate = form.getValues(`fields.${index}.value`);
                                                            if(!!value && !!firstReleaseDate && dayjs(value).isBefore(dayjs(firstReleaseDate))) {
                                                                if(index != -1) {
                                                                    form.setValue(`fields.${index}.value`, null, {shouldDirty: true});
                                                                    form.setValue(`fields.${index}.values`, [], {shouldDirty: true});
                                                                    setCargosPlaceOptions([]);
                                                                    
                                                                    const issuingPlaceIndex = attachmentFields.fields.findIndex((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUING_PLACE);
                                                                    if(issuingPlaceIndex != -1 ) {
                                                                        form.setValue(`fields.${issuingPlaceIndex}.value`, null, {shouldDirty: true});
                                                                        form.setValue(`fields.${issuingPlaceIndex}.values`, [], {shouldDirty: true});
                                                                    }
                                                                }

                                                            }
                                                        }

                                                        if(dayjs(value).isValid() && !attachmentFields.fields.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE) 
                                                        && attachmentFields.fields.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUING_PLACE)) {
                                                            handleIssuingPlaceByDate(value);
                                                            handleIssuingPlaceCountryByDate(value);
                                                        }
                                                        if(!value && !attachmentFields.fields.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE) 
                                                        && attachmentFields.fields.find((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUING_PLACE)) {
                                                            setCargosPlaceOptions([]);
                                                            setCargosPlaceCountryOptions([]);
                                                        }
                                                    }}
                                                    inputFormat={"DD/MM/YYYY"}
                                                    renderInput={(params: any) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            id="releaseDate"
                                                            label={t("releaseDate")}
                                                            required
                                                            error={Boolean(fieldState.error)}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}
                                                />
                                            )}
                                        />
                                    </Grid>
                                }
                                {!!currentKind && showFields.showExpireDate &&
                                    <Grid item md={3} xs={12}>
                                        <Controller 
                                            control={form.control}
                                            name="expireDate"
                                            render={({field, fieldState}) => (
                                                <DatePicker
                                                    value={field.value || null}
                                                    onChange={(value) => {
                                                        field.onChange(dayjs(value).format("YYYY-MM-DD"));

                                                        if(currentKind == CBDossierKindKeys.LICENCE) {
                                                            const index = attachmentFields.fields.findIndex((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUE_DATE);
                                                            const issueDate = form.getValues(`fields.${index}.value`);
                                                            if(!!value && !!issueDate && dayjs(value).isBefore(dayjs(issueDate))) {
                                                                if(index != -1) {
                                                                    form.setValue(`fields.${index}.value`, null, {shouldDirty: true});
                                                                    form.setValue(`fields.${index}.values`, [], {shouldDirty: true});
                                                                    setCargosPlaceOptions([]);

                                                                    const issuingPlaceIndex = attachmentFields.fields.findIndex((f) => f.type?.name == CBAttachmentFieldTypeNames.ISSUING_PLACE);
                                                                    if(issuingPlaceIndex != -1 ) {
                                                                        form.setValue(`fields.${issuingPlaceIndex}.value`, null, {shouldDirty: true});
                                                                        form.setValue(`fields.${issuingPlaceIndex}.values`, [], {shouldDirty: true});
                                                                    }
                                                                }

                                                            }
                                                        }
                                                    }}
                                                    inputFormat={"DD/MM/YYYY"}
                                                    //disabled={!currentReleaseDate}
                                                    minDate={!!currentReleaseDate ?  currentReleaseDate : undefined}
                                                    renderInput={(params: any) => (
                                                        <TextField
                                                            {...params}
                                                            fullWidth
                                                            id="expireDate"
                                                            label={t("expireDate")}
                                                            required
                                                            error={Boolean(fieldState.error)}
                                                            helperText={fieldState.error?.message}
                                                        />
                                                    )}
                                                /> 
                                            )}
                                        />
                                    </Grid>
                                }
                                {!!currentKind && showFields.showAttachmentFields &&
                                    attachmentFields.fields.map((f, index) => (
                                        <CutomerBaseAttachmentCustomFields
                                            key={`cb-dossier-${f.type?.name}-${index}`} 
                                            f={f}
                                            index={index}
                                            cargosPlaceOptions={cargosPlaceOptions}
                                            cargosPlaceCountryOptions={cargosPlaceCountryOptions}
                                            citizenshipCargosPlaceOptions={citizenshipCargosPlaceOptions}
                                            handleIssuingPlaceByDate={handleIssuingPlaceByDate}
                                            handleIssuingPlaceCountryByDate={handleIssuingPlaceCountryByDate}
                                            onIssueDateChange={(value) => {
                                                if(!value) setCargosPlaceOptions([]);
                                            }}
                                        />

                                    ))
                                }
                            </Grid>
                            {!!currentKind && 
                                <Grid item container id="file-data-row" md={12} xs={12} columnSpacing={3} rowSpacing={4}>
                                    <Grid item id="first-file-row" md={showFields.showBackPage ? 6 : 12} xs={12}>
                                        <Controller 
                                            control={form.control}
                                            name="frontPage"
                                            render={({field, fieldState}) => (
                                                <Fragment>
                                                    <FileUploader
                                                        acceptedFiles={{'image/png': ['.png'],'image/jpg': ['.jpg'],'image/jpeg': ['.jpeg']}}
                                                        uploadedFiles={field.value ? [field.value as File] : []}
                                                        label={t("documentModal.frontPageTitle") + ' *'}
                                                        onUpload={(frontPage: File) => {
                                                            frontPage.arrayBuffer().then((value) => {
                                                                if(isValidFileType(value)) {
                                                                    field.onChange(frontPage);
                                                                    form.setValue("isNewFrontPage", true);
                                                                } else {
                                                                    dispatchGlobalData({
                                                                        type: setSnackbar,
                                                                        payload: {
                                                                            snackbar: {
                                                                                message: t("message.error.invalidFileType"),
                                                                                type: snackbarTypes.error,
                                                                                show: true,
                                                                                autoClose: true
                                                                            }
                                                                        }
                                                                    });

                                                                    field.onChange(null);
                                                                }
                                                            }) 
                                                        }}
                                                        onRemove={() => field.onChange(null)}
                                                    />
                                                    {Boolean(fieldState.error) && <p style={{fontWeight: 400, fontSize: "0.75rem", color: "red"}}>{fieldState.error?.message}</p>}
                                                </Fragment>
                                                
                                            )}
                                        />
                                    </Grid>
                                    {showFields.showBackPage && 
                                        <Grid item id="last-file-row" md={6} xs={12}>
                                            <Controller 
                                                control={form.control}
                                                name="backPage"
                                                render={({field, fieldState}) => (
                                                    <>
                                                        <FileUploader
                                                            acceptedFiles={{'image/png': ['.png'],'image/jpg': ['.jpg'],'image/jpeg': ['.jpeg']}}
                                                            uploadedFiles={field.value ? [field.value as File] : []}
                                                            label={t("documentModal.backPageTitle") + ' *'}
                                                            onUpload={(backPage: File) => {
                                                                backPage.arrayBuffer().then((value) => {
                                                                    if(isValidFileType(value)) {
                                                                        field.onChange(backPage);
                                                                        form.setValue("isNewBackPage", true);
                                                                    } else {
                                                                        dispatchGlobalData({
                                                                            type: setSnackbar,
                                                                            payload: {
                                                                                snackbar: {
                                                                                    message: t("message.error.invalidFileType"),
                                                                                    type: snackbarTypes.error,
                                                                                    show: true,
                                                                                    autoClose: true
                                                                                }
                                                                            }
                                                                        });
            
                                                                        field.onChange(null);
                                                                    }
                                                                })
                                                            }}
                                                            onRemove={() => field.onChange(null)}
                                                        />
                                                        {Boolean(fieldState.error) && <p style={{fontWeight: 400, fontSize: "0.75rem", color: "red"}}>{fieldState.error?.message}</p>}
                                                    </>
                                                )}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                            }
                        </Grid>
                    }
                    </DialogContent>
                    <DialogActions sx={{backgroundColor: theme.palette.neutral.bright, padding: "15px"}}>
                        <Stack direction="row" columnGap={1}>
                            <Button
                                variant="outlined"
                                disableElevation
                                color="accent"
                                sx={{backgroundColor: theme.palette.neutral.bright}}
                                onClick={onClose}
                            >
                            <Typography fontWeight={"bold"} variant="subtitle2" color="accent" lineHeight={0}>
                                        {t("documentModal.cancelBtn")}
                                </Typography> 
                            </Button>
                            <Button
                                variant="contained"
                                color="accent"
                                disableElevation
                                onClick={form.handleSubmit(onSave)}
                            >
                                <Typography fontWeight={"bold"} variant="subtitle2" color="accent" lineHeight={0}>
                                        {t("documentModal.confirmBtn")}
                                </Typography>
                            </Button>
                        </Stack>
                    </DialogActions>
                </Dialog>
            </FormProvider>
            <CustomLoader loading={loadingGetCargosPlaces} />
        </>
    );

}

export default DocumentModal;